<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNomeDoAgente } from 'dashboard/composables/useNomeDoAgente';
import { useMapGetter } from 'dashboard/composables/store.js';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import { etiquetaCasa, nomeDaEtiqueta } from 'dashboard/helper/nomeDaEtiqueta';

const props = defineProps({
  show: { type: Boolean, default: false },
  // Se o pai não tem filtro aplicado (ex.: limpou pelo X do header),
  // o drawer reabre zerado em vez de mostrar seleção fantasma.
  hasActiveFilters: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'apply', 'clear', 'openAdvanced']);

const { t } = useI18n();
const { nomeDoAgente } = useNomeDoAgente();

const labels = useMapGetter('labels/getLabels');
const teams = useMapGetter('teams/getTeams');
const inboxes = useMapGetter('inboxes/getInboxes');

// Toggles (client-side: advup não expõe filtro backend de não-lidas)
const unreadOnly = ref(false);
const incomingOnly = ref(false);
const clientOnly = ref(false);
const followUpOnly = ref(false);
// advup — 2026-07-28 (pedido do Victor): ver o que está na fila da Andie
// (assignee_agent_bot preenchido) — hoje só ela existe como AgentBot na
// conta, então "tem bot atribuído" já equivale a "fila da Andie".
const andieQueueOnly = ref(false);

// Selects
const selectedLabels = ref([]);
const selectedTeam = ref('');
const selectedInbox = ref('');

// As etiquetas ficam À VISTA, como chips que ligam e desligam. Eram um
// dropdown dentro do corpo que rola: com a gaveta cheia a lista abria cortada
// pelo fim do painel, e para ver o que estava marcado era preciso abrir. A
// busca só aparece quando a lista passa do que se lê de relance.
const LIMITE_SEM_BUSCA = 10;
const tagSearch = ref('');
const mostraBuscaDeTags = computed(
  () => labels.value.length > LIMITE_SEM_BUSCA
);

const chaves = computed(() => [
  {
    modelo: unreadOnly,
    nome: t('CHAT_LIST.FILTER_DRAWER.UNREAD'),
    sub: t('CHAT_LIST.FILTER_DRAWER.UNREAD_HINT'),
  },
  {
    modelo: incomingOnly,
    nome: t('CHAT_LIST.FILTER_DRAWER.INCOMING'),
    sub: t('CHAT_LIST.FILTER_DRAWER.INCOMING_HINT'),
  },
  {
    modelo: clientOnly,
    nome: t('CHAT_LIST.FILTER_DRAWER.CLIENT_ONLY'),
    sub: t('CHAT_LIST.FILTER_DRAWER.CLIENT_ONLY_HINT'),
  },
  {
    modelo: followUpOnly,
    nome: t('CHAT_LIST.FILTER_DRAWER.FOLLOW_UP_ONLY'),
    sub: t('CHAT_LIST.FILTER_DRAWER.FOLLOW_UP_ONLY_HINT'),
  },
  {
    modelo: andieQueueOnly,
    nome: t('CHAT_LIST.FILTER_DRAWER.ANDIE_QUEUE_ONLY', {
      agente: nomeDoAgente.value,
    }),
    sub: t('CHAT_LIST.FILTER_DRAWER.ANDIE_QUEUE_ONLY_HINT'),
  },
]);

// Primeira opção desfaz a escolha: o AdvupSelect não tem "limpar", e sem ela
// quem escolheu um departamento só voltava a "todos" pelo Limpar geral.
const opcoesDeTime = computed(() => [
  { label: t('CHAT_LIST.FILTER_DRAWER.ANY'), value: '' },
  ...teams.value.map(time => ({ label: time.name, value: time.id })),
]);

const opcoesDeCaixa = computed(() => [
  { label: t('CHAT_LIST.FILTER_DRAWER.ANY'), value: '' },
  ...inboxes.value.map(caixa => ({ label: caixa.name, value: caixa.id })),
]);

const filteredLabels = computed(() => {
  const q = tagSearch.value.trim();
  if (!q) return labels.value;
  return labels.value.filter(l => etiquetaCasa(l.title, q));
});

// Snapshot do último filtro aplicado (pra reabrir o drawer já preenchido).
const applied = ref(null);

const reset = () => {
  unreadOnly.value = false;
  incomingOnly.value = false;
  clientOnly.value = false;
  followUpOnly.value = false;
  andieQueueOnly.value = false;
  selectedLabels.value = [];
  selectedTeam.value = '';
  selectedInbox.value = '';
  tagSearch.value = '';
};

const restoreFromApplied = () => {
  const a = applied.value;
  unreadOnly.value = !!a?.unreadOnly;
  incomingOnly.value = !!a?.incomingOnly;
  clientOnly.value = !!a?.clientOnly;
  followUpOnly.value = !!a?.followUpOnly;
  andieQueueOnly.value = !!a?.andieQueueOnly;
  selectedLabels.value = [...(a?.labels || [])];
  selectedTeam.value = a?.team || '';
  selectedInbox.value = a?.inbox || '';
  tagSearch.value = '';
};

// Ao abrir: restaura o último filtro aplicado; se o pai não tem mais
// filtro ativo (limpou externamente), zera o snapshot e abre limpo.
watch(
  () => props.show,
  isOpen => {
    if (!isOpen) return;
    if (!props.hasActiveFilters) applied.value = null;
    if (applied.value) restoreFromApplied();
    else reset();
  }
);

const toggleLabel = title => {
  const idx = selectedLabels.value.indexOf(title);
  if (idx === -1) selectedLabels.value.push(title);
  else selectedLabels.value.splice(idx, 1);
};

// Quantos filtros estão escolhidos — o topo diz o número, e o botão de
// aplicar só acende com pelo menos um.
const quantosEscolhidos = computed(
  () =>
    chaves.value.filter(chave => chave.modelo.value).length +
    selectedLabels.value.length +
    (selectedTeam.value ? 1 : 0) +
    (selectedInbox.value ? 1 : 0)
);
const hasAnyFilter = computed(() => quantosEscolhidos.value > 0);

const buildServerPayload = () => {
  const payload = [];
  const base = { query_operator: 'and', custom_attribute_type: '' };
  // Toggles agora têm filtro backend dedicado (subquery em messages).
  if (unreadOnly.value) {
    payload.push({
      attribute_key: 'unread',
      filter_operator: 'is_present',
      values: [],
      ...base,
    });
  }
  if (incomingOnly.value) {
    payload.push({
      attribute_key: 'last_incoming',
      filter_operator: 'is_present',
      values: [],
      ...base,
    });
  }
  if (clientOnly.value) {
    payload.push({
      attribute_key: 'is_legal_client',
      filter_operator: 'equal_to',
      values: [true],
      query_operator: 'and',
      custom_attribute_type: 'contact_attribute',
    });
  }
  if (followUpOnly.value) {
    payload.push({
      attribute_key: 'follow_up',
      filter_operator: 'is_present',
      values: [],
      ...base,
    });
  }
  if (andieQueueOnly.value) {
    payload.push({
      attribute_key: 'assignee_agent_bot_id',
      filter_operator: 'is_present',
      values: [],
      ...base,
    });
  }
  if (selectedLabels.value.length) {
    payload.push({
      attribute_key: 'labels',
      filter_operator: 'equal_to',
      values: [...selectedLabels.value],
      ...base,
    });
  }
  if (selectedTeam.value) {
    payload.push({
      attribute_key: 'team_id',
      filter_operator: 'equal_to',
      values: [selectedTeam.value],
      ...base,
    });
  }
  if (selectedInbox.value) {
    payload.push({
      attribute_key: 'inbox_id',
      filter_operator: 'equal_to',
      values: [selectedInbox.value],
      ...base,
    });
  }
  return payload;
};

const onApply = () => {
  if (!hasAnyFilter.value) return;
  applied.value = {
    unreadOnly: unreadOnly.value,
    incomingOnly: incomingOnly.value,
    clientOnly: clientOnly.value,
    followUpOnly: followUpOnly.value,
    andieQueueOnly: andieQueueOnly.value,
    labels: [...selectedLabels.value],
    team: selectedTeam.value,
    inbox: selectedInbox.value,
  };
  emit('apply', buildServerPayload());
};

const onClear = () => {
  reset();
  applied.value = null;
  emit('clear');
};
</script>

<template>
  <!-- No `body`: dentro da coluna da lista, qualquer ancestral com `overflow`
       ou `transform` vira a referência do `fixed` e corta a gaveta. -->
  <Teleport to="body">
    <Transition name="cfd">
      <div
        v-if="show"
        class="advup-modal-fundo cfd-fundo"
        @click.self="emit('close')"
      >
        <aside
          v-modal-acessivel="() => emit('close')"
          class="cfd-painel advup-modal--claro"
          aria-labelledby="cfd-titulo"
        >
          <!-- Topo claro da Agenda (§5.10), como os modais da conversa — era o
               degradê escuro (05/10/2026). -->
          <header class="advup-modal__topo">
            <i
              class="advup-modal__marca i-lucide-list-filter"
              aria-hidden="true"
            />
            <span class="advup-modal__selo" aria-hidden="true">
              <i class="i-lucide-list-filter" />
            </span>
            <div class="cfd-topo__txt">
              <h2 id="cfd-titulo" class="advup-modal__titulo">
                {{ t('CHAT_LIST.FILTER_DRAWER.TITLE') }}
              </h2>
              <p class="advup-modal__sub" aria-live="polite">
                {{
                  t('CHAT_LIST.FILTER_DRAWER.CHOSEN', quantosEscolhidos, {
                    count: quantosEscolhidos,
                  })
                }}
              </p>
            </div>
            <button
              type="button"
              class="advup-modal__fechar"
              :aria-label="t('CHAT_LIST.FILTER_DRAWER.CLOSE')"
              @click="emit('close')"
            >
              <i class="i-lucide-x" />
            </button>
          </header>

          <div class="advup-modal__corpo cfd-corpo">
            <section class="cfd-bloco" aria-labelledby="cfd-so">
              <p id="cfd-so" class="advup-rotulo advup-rotulo--secao">
                {{ t('CHAT_LIST.FILTER_DRAWER.SHOW_ONLY') }}
              </p>
              <button
                v-for="chave in chaves"
                :key="chave.nome"
                type="button"
                role="switch"
                class="advup-chave cfd-chave"
                :aria-checked="chave.modelo.value"
                @click="chave.modelo.value = !chave.modelo.value"
              >
                <span class="advup-chave__trilho" aria-hidden="true" />
                <span class="advup-chave__txt">
                  <span class="advup-chave__nome">{{ chave.nome }}</span>
                  <span class="advup-chave__sub">{{ chave.sub }}</span>
                </span>
              </button>
            </section>

            <section
              v-if="labels.length"
              class="cfd-bloco"
              aria-labelledby="cfd-tags"
            >
              <p id="cfd-tags" class="advup-rotulo advup-rotulo--secao">
                {{ t('CHAT_LIST.FILTER_DRAWER.TAGS') }}
              </p>
              <label
                v-if="mostraBuscaDeTags"
                class="advup-busca advup-busca--bloco"
              >
                <i
                  class="i-lucide-search advup-busca__lupa"
                  aria-hidden="true"
                />
                <input
                  v-model="tagSearch"
                  type="search"
                  class="advup-busca__campo reset-base"
                  :placeholder="t('CHAT_LIST.FILTER_DRAWER.SEARCH_TAGS')"
                  :aria-label="t('CHAT_LIST.FILTER_DRAWER.SEARCH_TAGS')"
                />
              </label>
              <div class="cfd-tags">
                <button
                  v-for="label in filteredLabels"
                  :key="label.id"
                  type="button"
                  class="cfd-tag"
                  :class="{
                    'cfd-tag--on': selectedLabels.includes(label.title),
                  }"
                  :aria-pressed="selectedLabels.includes(label.title)"
                  @click="toggleLabel(label.title)"
                >
                  <i
                    v-if="selectedLabels.includes(label.title)"
                    class="i-lucide-check cfd-tag__visto"
                    aria-hidden="true"
                  />
                  <span
                    v-else
                    class="cfd-tag__ponto"
                    :style="{ backgroundColor: label.color }"
                    aria-hidden="true"
                  />
                  <span
                    class="cfd-tag__nome"
                    :title="nomeDaEtiqueta(label.title)"
                  >
                    {{ nomeDaEtiqueta(label.title) }}
                  </span>
                </button>
                <p v-if="!filteredLabels.length" class="advup-nota">
                  {{ t('CHAT_LIST.FILTER_DRAWER.NO_TAGS_FOUND') }}
                </p>
              </div>
            </section>

            <section
              v-if="teams.length || inboxes.length"
              class="cfd-bloco"
              aria-labelledby="cfd-onde"
            >
              <p id="cfd-onde" class="advup-rotulo advup-rotulo--secao">
                {{ t('CHAT_LIST.FILTER_DRAWER.WHERE') }}
              </p>
              <div v-if="teams.length" class="cfd-campo">
                <span class="advup-rotulo">
                  {{ t('CHAT_LIST.FILTER_DRAWER.DEPARTMENT') }}
                </span>
                <AdvupSelect
                  v-model="selectedTeam"
                  :options="opcoesDeTime"
                  :rotulo="t('CHAT_LIST.FILTER_DRAWER.DEPARTMENT')"
                  min-width="100%"
                />
              </div>
              <div v-if="inboxes.length" class="cfd-campo">
                <span class="advup-rotulo">
                  {{ t('CHAT_LIST.FILTER_DRAWER.CONNECTION') }}
                </span>
                <AdvupSelect
                  v-model="selectedInbox"
                  :options="opcoesDeCaixa"
                  :rotulo="t('CHAT_LIST.FILTER_DRAWER.CONNECTION')"
                  min-width="100%"
                />
              </div>
            </section>

            <!-- "Filtros avançados" era um link azul sublinhado no meio do
                 formulário — lia como texto de ajuda, não como o caminho para
                 a tela que abre. -->
            <button
              type="button"
              class="advup-btn advup-btn--vazado cfd-avancados"
              @click="emit('openAdvanced')"
            >
              <i class="i-lucide-sliders-horizontal" aria-hidden="true" />
              {{ t('CHAT_LIST.FILTER_DRAWER.ADVANCED') }}
            </button>
          </div>

          <footer class="advup-modal__rodape advup-modal__rodape--acoes">
            <button
              type="button"
              class="advup-btn advup-modal__espaco"
              :disabled="!hasAnyFilter && !hasActiveFilters"
              @click="onClear"
            >
              {{ t('CHAT_LIST.FILTER_DRAWER.CLEAR') }}
            </button>
            <button
              type="button"
              class="advup-btn-forte"
              :disabled="!hasAnyFilter"
              @click="onApply"
            >
              <i class="i-lucide-check" aria-hidden="true" />
              {{ t('CHAT_LIST.FILTER_DRAWER.APPLY') }}
            </button>
          </footer>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* A GAVETA DE FILTROS da lista de conversas.
 *
 * Véu, topo, corpo e rodapé são os do modal (`.advup-modal-fundo`,
 * `__topo`, `__corpo`, `__rodape`): a gaveta é um modal que chega pela
 * lateral, e escrever os dela à mão era o que a deixava com outro véu (preto
 * a 40%, sem desfoque), outro título e outro rodapé. Aqui mora só o que é da
 * gaveta — o lado, a altura e a chegada. */
.cfd-fundo {
  justify-content: flex-start;
  align-items: stretch;
  padding: 0;
  overflow: hidden;
}

.cfd-painel {
  display: flex;
  flex-direction: column;
  width: 360px;
  max-width: 88vw;
  height: 100%;
  background: rgb(var(--solid-1));
  box-shadow: 0 24px 48px -12px rgb(15 23 42 / 0.4);
}

.cfd-topo__txt {
  flex: 1;
  min-width: 0;
}

.cfd-corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-5);
}

.cfd-bloco {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
}

.cfd-bloco > .advup-rotulo--secao {
  margin: 0;
}

/* As cinco chaves são uma pilha só: o vão menor que o dos blocos diz que
   elas andam juntas. */
.cfd-chave + .cfd-chave {
  margin-top: calc(-1 * var(--esp-1));
}

.cfd-tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-2);
}

/* O chip da etiqueta. Desligado, o ponto na cor da etiqueta diz qual é;
   ligado, o ponto vira o visto e o chip acende no azul — a cor da etiqueta
   não serve de estado, porque cada etiqueta tem uma. */
.cfd-tag {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-2);
  max-width: 100%;
  height: 32px;
  padding: 0 var(--esp-3);
  border: 1px solid rgb(var(--slate-5));
  border-radius: var(--r-redondo);
  background: rgb(var(--solid-1));
  font-size: var(--txt-nota);
  font-weight: var(--peso-dado);
  color: rgb(var(--slate-12));
  cursor: pointer;
  transition:
    border-color var(--mov-1) var(--mov-curva),
    background-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva),
    transform var(--mov-2) var(--mov-curva-pulo);
}

.cfd-tag:hover {
  border-color: rgb(var(--blue-7));
  transform: translateY(-1px);
}

.cfd-tag:active {
  transform: scale(0.97);
}

.cfd-tag:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.cfd-tag--on {
  border-color: rgb(var(--blue-7));
  background: rgb(var(--blue-3));
  color: rgb(var(--blue-11));
}

.cfd-tag__ponto {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--r-redondo);
}

.cfd-tag__visto {
  flex-shrink: 0;
  font-size: var(--ico-1);
  animation: advupPop var(--mov-2) var(--mov-curva-pulo) backwards;
}

.cfd-tag__nome {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cfd-campo {
  display: flex;
  flex-direction: column;
}

.cfd-avancados {
  align-self: center;
  color: rgb(var(--blue-11));
}

/* A CHEGADA: o véu clareia e o painel desliza do lado da lista, com o mesmo
   tempo do resto do produto. Na saída, o inverso — sem ela o painel sumia de
   estalo. */
.cfd-enter-active,
.cfd-leave-active {
  transition: opacity var(--mov-3) var(--mov-curva);
}

.cfd-enter-active .cfd-painel,
.cfd-leave-active .cfd-painel {
  transition: transform var(--mov-3) var(--mov-curva);
}

.cfd-enter-from,
.cfd-leave-to {
  opacity: 0;
}

.cfd-enter-from .cfd-painel,
.cfd-leave-to .cfd-painel {
  transform: translateX(-100%);
}

[dir='rtl'] .cfd-enter-from .cfd-painel,
[dir='rtl'] .cfd-leave-to .cfd-painel {
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .cfd-tag,
  .cfd-tag:hover,
  .cfd-tag:active {
    transition: none;
    transform: none;
  }

  .cfd-tag__visto {
    animation: none;
  }
}
</style>
